import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import MobileShell from '@/components/MobileShell';
import BottomNav from '@/components/BottomNav';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/serviceTimer';
import {
  bookingStatusLabel,
  caseTypeDisplay,
  durationDisplay,
  emptyStateMessage,
  formatBookingDate,
  historySectionTabs,
  primaryDisplayAmount,
} from '@/lib/bookingHistory';

function BookingCard({ booking }) {
  const navigate = useNavigate();
  const duration = durationDisplay(booking);
  const caseLabel = caseTypeDisplay(booking.booking_requirements);
  const amount = primaryDisplayAmount(booking);
  const currency = booking.quote?.currency || booking.amounts?.currency || 'INR';
  const dateLabel = formatBookingDate(
    booking.completed_at || booking.service_started_at || booking.created_at,
  );

  return (
    <div className="resqly-card p-4 space-y-2" data-testid={`booking-card-${booking.id}`}>
      <div className="font-semibold text-slate-900">{booking.service_label || booking.service_key}</div>
      {duration && (
        <div className="text-sm text-slate-700" data-testid="booking-duration">{duration}</div>
      )}
      {caseLabel && (
        <div className="text-sm text-slate-600" data-testid="booking-case-type">{caseLabel}</div>
      )}
      {booking.provider_name && (
        <div className="text-sm text-slate-600">Provider: {booking.provider_name}</div>
      )}
      <div className="text-sm font-medium text-blue-700" data-testid="booking-status-label">
        {bookingStatusLabel(booking)}
      </div>
      {amount != null && (
        <div className="text-sm font-semibold text-slate-900" data-testid="booking-amount">
          {formatCurrency(amount, currency)}
        </div>
      )}
      {dateLabel && (
        <div className="text-xs text-slate-500" data-testid="booking-date">{dateLabel}</div>
      )}
      <div className="flex flex-wrap gap-2 pt-2">
        <Button
          variant="outline"
          size="sm"
          data-testid="booking-view-detail"
          onClick={() => navigate(`/consumer/bookings/${booking.id}`)}
        >
          View
        </Button>
        {booking.actions?.can_track && (
          <Button
            size="sm"
            data-testid="booking-track-btn"
            onClick={() => navigate(`/consumer/bookings/${booking.id}/tracking`)}
          >
            Track
          </Button>
        )}
        {booking.actions?.can_pay_base && (
          <Button
            size="sm"
            data-testid="booking-pay-base-btn"
            onClick={() => navigate(`/consumer/bookings/${booking.id}/payment`)}
          >
            Pay Now
          </Button>
        )}
        {booking.actions?.can_pay_extra && (
          <Button
            size="sm"
            data-testid="booking-pay-extra-btn"
            onClick={() => navigate(`/consumer/bookings/${booking.id}/extra-payment`)}
          >
            Pay Extra
          </Button>
        )}
      </div>
    </div>
  );
}

export default function ConsumerBookings() {
  const [section, setSection] = useState('active');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const load = async (nextPage = 1, replace = true) => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/consumers/me/bookings?section=${section}&page=${nextPage}&page_size=20`,
      );
      setBookings((prev) => (replace ? data.bookings : [...prev, ...data.bookings]));
      setHasMore(data.has_more);
      setPage(nextPage);
    } catch {
      if (replace) setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1, true);
  }, [section]);

  return (
    <div className="resqly-shell">
      <div className="resqly-frame flex flex-col min-h-screen">
        <MobileShell title="My Bookings">
          <div className="px-5 py-5 space-y-4" data-testid="consumer-bookings">
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {historySectionTabs().map((tab) => (
                <button
                  key={tab.key}
                  data-testid={`bookings-tab-${tab.key}`}
                  onClick={() => setSection(tab.key)}
                  className={`px-3 py-1.5 rounded-full text-sm border whitespace-nowrap ${
                    section === tab.key
                      ? 'bg-blue-700 text-white border-blue-700'
                      : 'bg-white text-slate-700 border-slate-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="text-sm text-slate-500">Loading bookings…</div>
            ) : bookings.length === 0 ? (
              <div
                className="resqly-card p-6 text-center text-sm text-slate-500"
                data-testid="bookings-empty"
              >
                {emptyStateMessage(section)}
              </div>
            ) : (
              <div className="space-y-3">
                {bookings.map((b) => <BookingCard key={b.id} booking={b} />)}
                {hasMore && (
                  <Button
                    variant="outline"
                    className="w-full"
                    data-testid="bookings-load-more"
                    onClick={() => load(page + 1, false)}
                  >
                    Load more
                  </Button>
                )}
              </div>
            )}
          </div>
        </MobileShell>
        <BottomNav />
      </div>
    </div>
  );
}
